import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Form, Input, Spin, Alert, Button, Row, Col, Select, InputNumber, Checkbox, message } from 'antd';
import { theme } from '../../theme';

const { Option } = Select;
interface OrigenData {
  id: number;
  nc: string;
  denominacion: string;
}

interface ClienteData {
  id: number;
  nc: string;
  denominacion: string;
}

interface ContratoData {
  id: number;
  ncontrato: string;
  tipo: string;
  producto_nombre: string;
  nproducto_id: number;
  fecha: string;
  comprador_nombre: string;
  comprador_id: number;
  vendedor_nombre: string;
  vendedor_id: number;
  despachante_nombre: string;
  corredor_nombre: string;
  entregador_nombre: string;
  destinatario_nombre: string;
  empresa_nombre: string;
  estado: string;
  tarafardo: number;
  kilos: number;
  precio: number;
  fecha_contrato: string;
  fecha_cierre: string;
}

interface ItemDetalle {
  id: number;
  desde: number;
  hasta: number;
  lote: number;
  descartado: boolean;
  motivo?: string;
  id_origen: number;  // Agregar el origen que estaba seleccionado en ese momento
  modificable: boolean; // Indica si el fardo se puede modificar (checkbox del backend)
  // Campos adicionales del backend
  aplicacion: 'SI' | 'NO';
  stock: string;
  checkbox: boolean;
  id_fardo?: number;  // ID del fardo que viene del backend
  id_orden?: number;  // ID de la orden que viene del backend
}

interface ResumenItem {
  desde: number;
  hasta: number;
  lote: number;
  cantidad: number;
}

interface FardoParaGrabar {
  nro_fardo: number;
  nro_lote: number;
  id_fardo: number;
  id_orden?: number;  // ID de la orden que viene del backend (opcional)
  origen_id: number;  // Cambiar de id_origen a origen_id para coincidir con el backend
  cliente_id: number;  // Cambiar de id_cliente a cliente_id para coincidir con el backend
}

// New interface for the backend response
interface FardoAplicadoResponse {
  nro_fardo: number;
  nro_lote: number;
  aplicacion: 'SI' | 'NO';
  stock: string;
  checkbox: boolean;
  id_fardo?: number;  // Campo opcional que viene del backend
  id_orden?: number;  // Campo opcional que viene del backend
}


const AltaAplicacion: React.FC = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [contratoData, setContratoData] = useState<ContratoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [itemsDetalle, setItemsDetalle] = useState<ItemDetalle[]>([]);
  const [resumenItems, setResumenItems] = useState<ResumenItem[]>([]);
  const [grabandoDatos, setGrabandoDatos] = useState(false);
  const [origenesOptions, setOrigenesOptions] = useState<{value: string, label: string}[]>([]);
  const [clientesOptions, setClientesOptions] = useState<{value: string, label: string}[]>([]);
  const [fardosParaGrabar, setFardosParaGrabar] = useState<FardoParaGrabar[]>([]);

  useEffect(() => {
    const obtenerDatosContrato = async () => {
      try {
        if (!isAuthenticated) {
          setError('Por favor, inicie sesión para ver los datos del contrato');
          setLoading(false);
          return;
        }

        const id_empresa = user?.id_empresa;
        if (!id_empresa) {
          setError('No se encontró la empresa asociada a su usuario');
          setLoading(false);
          return;
        }

        const id_contrato = location.state?.id_contrato;
        if (!id_contrato || typeof id_contrato !== 'number') {
          setError('No se encontró el ID del contrato');
          setLoading(false);
          return;
        }

        const token = await getAccessTokenSilently();
        
        // Cargar datos del contrato
        const response = await fetch(
          `${process.env.REACT_APP_ORDENES_URL}/contrato_aplicacion/${id_empresa}/${id_contrato}/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Error al cargar los datos del contrato');
        }

        const datos = await response.json();
        console.log('Datos del contrato:', datos);
        setContratoData(datos);

        // Cargar datos del padrón (una sola vez para origen y cliente)
        const padronResponse = await fetch(
          `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (padronResponse.ok) {
          const padronData = await padronResponse.json();
          
          // Configurar opciones para los selectores
          const opcionesPadron = padronData.map((item: any) => ({
            value: item.id.toString(),
            label: `${item.nc} - ${item.denominacion}`
          }));
          
          setOrigenesOptions(opcionesPadron);
          setClientesOptions(opcionesPadron);
          
          // Lógica específica según el tipo de contrato para establecer valores por defecto
          if (datos.tipo === 'C') {
            // Para contratos de compra: Origen = Vendedor, Cliente = Comprador
            if (datos.vendedor_id) {
              const vendedorEncontrado = padronData.find((item: any) => item.id === datos.vendedor_id);
              if (vendedorEncontrado) {
                form.setFieldsValue({
                  origen: vendedorEncontrado.id.toString()
                });
                console.log('Origen establecido con vendedor para contrato de compra:', vendedorEncontrado.denominacion);
              }
            }
            
            if (datos.comprador_id) {
              const compradorEncontrado = padronData.find((item: any) => item.id === datos.comprador_id);
              if (compradorEncontrado) {
                form.setFieldsValue({
                  cliente: compradorEncontrado.id.toString()
                });
                console.log('Cliente establecido con comprador para contrato de compra:', compradorEncontrado.denominacion);
              }
            }
          } else {
            // Para otros tipos de contrato, usar el registro predeterminado
            const registroPredeterminado = padronData.find((item: any) => item.nc === '000000');
            
            if (registroPredeterminado) {
              // Establecer tanto origen como cliente con el mismo valor predeterminado
              form.setFieldsValue({
                origen: registroPredeterminado.id.toString(),
                cliente: registroPredeterminado.id.toString()
              });
            }
          }
        } else {
          message.error('No se pudieron cargar los datos del padrón');
        }
        
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido');
        setLoading(false);
      }
    };

    obtenerDatosContrato();
  }, [getAccessTokenSilently, user, isAuthenticated, location.state, form]);

  const handleSubmit = async (values: any) => {
    console.log('handleSubmit ejecutado con valores:', values);
    console.log('Tipo de contrato:', contratoData?.tipo);
    console.log('Origen seleccionado:', values.origen);
    console.log('Cliente seleccionado:', values.cliente);
    console.log('Tipo de origen:', typeof values.origen);
    console.log('Tipo de cliente:', typeof values.cliente);
    console.log('Origen parseado:', parseInt(values.origen));
    
    // Validar que el origen esté presente
    if (!values.origen) {
      message.error('Debe seleccionar un origen antes de enviar');
      return;
    }
    
    // Validar que el cliente esté presente (excepto para contratos de compra donde está deshabilitado)
    if (contratoData?.tipo !== 'C' && !values.cliente) {
      console.log('Error: Cliente no seleccionado. Tipo contrato:', contratoData?.tipo, 'Cliente:', values.cliente);
      message.error('Debe seleccionar un cliente antes de enviar');
      return;
    }
    
    const idOrigen = parseInt(values.origen);
    if (isNaN(idOrigen)) {
      message.error('El origen seleccionado no es válido');
      console.error('Error: idOrigen es NaN. values.origen:', values.origen);
      return;
    }
    
    // Obtener el id del cliente seleccionado
    const idCliente = values.cliente ? parseInt(values.cliente) : 0;
    console.log('idCliente calculado:', idCliente, 'Tipo contrato:', contratoData?.tipo);
    if (contratoData?.tipo !== 'C' && isNaN(idCliente)) {
      message.error('El cliente seleccionado no es válido');
      console.error('Error: idCliente es NaN. values.cliente:', values.cliente);
      return;
    }
    
    try {
      const desde = values.desde;
      const hasta = values.hasta;
      const lote = values.lote;

      // Validar que desde sea menor o igual que hasta
      if (desde > hasta) {
        message.error('El valor "Desde" no puede ser mayor que "Hasta"');
        return;
      }

      // Verificar duplicados solo contra fardos ya resumidos
      // Esto permite rectificar errores en el detalle sin problemas
      const fardosYaResumidos = resumenItems.flatMap(item => {
        const fardos = [];
        for (let fardo = item.desde; fardo <= item.hasta; fardo++) {
          fardos.push(fardo);
        }
        return fardos;
      });
      
      console.log('Fardos ya resumidos:', fardosYaResumidos);
      console.log('Rango a verificar:', { desde, hasta });
      
      const fardosDuplicadosResumidos = [];
      
      // Verificar cada fardo en el rango solo contra fardos ya resumidos
      for (let fardo = desde; fardo <= hasta; fardo++) {
        if (fardosYaResumidos.includes(fardo)) {
          fardosDuplicadosResumidos.push(fardo);
        }
      }

      // Si hay duplicados ya resumidos, mostrar error y no continuar
      if (fardosDuplicadosResumidos.length > 0) {
        console.log('Duplicados ya resumidos:', fardosDuplicadosResumidos);
        message.error(`Error: Los siguientes fardos ya fueron resumidos anteriormente y no se pueden volver a procesar: ${fardosDuplicadosResumidos.join(', ')}`);
        return;
      }

      const nuevosItems: ItemDetalle[] = [];

      // Consultar fardos aplicados usando la nueva función
      console.log('Consultando fardos aplicados...');
      const token = await getAccessTokenSilently();
      const id_empresa = user?.id_empresa;
      const id_sucursal = user?.id_sucursal;
      if (!id_empresa) {
        message.error('No se encontró la empresa asociada a su usuario');
        return;
      }

      // Validar que tenemos el id del contrato
      if (!contratoData?.id) {
        message.error('No se encontró el ID del contrato');
        return;
      }

      const url = `${process.env.REACT_APP_ORDENES_URL}/fardos_contrato_aplicados/${id_empresa}/${id_sucursal}/${contratoData.id}/${desde}/${hasta}/${lote}/${idOrigen}/${idCliente}/`;
      console.log('Consultando URL:', url);

      // Consultar al backend sobre los fardos aplicados
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en la respuesta:', errorText);
        throw new Error('Error al consultar los fardos aplicados');
      }

      const data = await response.json();
      console.log('Fardos aplicados:', data);

      // Procesar los resultados de fardos_contrato_aplicados
      const fardosAplicadosData: FardoAplicadoResponse[] = data;
      console.log('Fardos aplicados data (nuevo formato):', fardosAplicadosData);
      console.log('Ejemplo de fardo:', fardosAplicadosData[0]);

      // Crear un mapa para acceso rápido por número de fardo
      const fardosMap = new Map<number, FardoAplicadoResponse>();
      fardosAplicadosData.forEach(fardo => {
        fardosMap.set(fardo.nro_fardo, fardo);
      });

      // Generar la lista de fardos desde hasta
      // El backend ahora devuelve:
      // - 'aplicacion': 'SI' si está aplicado al contrato, 'NO' si no
      // - 'checkbox': true si se puede reformar (no aplicado), false si no se puede (aplicado)
      // - 'stock': información sobre el stock (puede estar vacío)
      // - Los fardos con checkbox=false NO se pueden modificar (ya están aplicados)
      for (let fardo = desde; fardo <= hasta; fardo++) {
        const fardoData = fardosMap.get(fardo);
        
        if (fardoData) {
          // El fardo existe en la respuesta del backend
          nuevosItems.push({
            id: Date.now() + fardo,
            desde: fardo,
            hasta: fardo,
            lote: fardoData.nro_lote, // Usar el lote del backend
            descartado: !fardoData.checkbox, // Usar el checkbox del backend
            motivo: fardoData.aplicacion === 'SI' ? 'Aplicado' : 'No aplicado',
            id_origen: idOrigen,
            modificable: fardoData.checkbox, // Asignar la propiedad modificable
            aplicacion: fardoData.aplicacion,
            stock: fardoData.stock,
            checkbox: fardoData.checkbox,
            id_fardo: fardoData.id_fardo || 0, // Asignar el id_fardo del backend
            id_orden: fardoData.id_orden || 0 // Asignar el id_orden del backend
          });
        } else {
          // El fardo no existe en la respuesta (caso edge)
          nuevosItems.push({
            id: Date.now() + fardo,
            desde: fardo,
            hasta: fardo,
            lote,
            descartado: true, // Por defecto descartado si no está en la respuesta
            motivo: 'No encontrado',
            id_origen: idOrigen,
            modificable: true, // Por defecto modificable si no está en la respuesta
            aplicacion: 'NO',
            stock: '',
            checkbox: true,
            id_fardo: 0, // No hay id_fardo si no está en la respuesta
            id_orden: 0 // No hay id_orden si no está en la respuesta
          });
        }
      }

      // Actualizar el estado con los nuevos items
      setItemsDetalle(prevItems => [...prevItems, ...nuevosItems]);

      // Limpiar el formulario
      form.resetFields(['desde', 'hasta', 'lote']);
      message.success(`Se agregaron ${nuevosItems.length} fardos al detalle`);
    } catch (error) {
      console.error('Error al procesar el formulario:', error);
      message.error('Error al procesar los datos');
    }
  };

  // Función para contar los items no descartados (solo modificables)
  const contarItemsNoDescartados = () => {
    return itemsDetalle.filter(item => !item.descartado && item.modificable).length;
  };

  const onFinish = (values: any) => {
    handleSubmit(values);
    return false; // Previene el comportamiento por defecto del formulario
  };

  const handleDescartarItem = (id: number) => {
    setItemsDetalle(itemsDetalle.map(item => 
      item.id === id && item.modificable ? { ...item, descartado: !item.descartado } : item
    ));
  };

  const handleTildarTodos = () => {
    setItemsDetalle(itemsDetalle.map(item => 
      item.modificable ? { ...item, descartado: false } : item
    ));
  };

  const handleDestildarTodos = () => {
    setItemsDetalle(itemsDetalle.map(item => 
      item.modificable ? { ...item, descartado: true } : item
    ));
  };

  const handleResumir = () => {
    // Verificar si hay items para resumir (solo modificables)
    const itemsNoDescartados = itemsDetalle.filter(item => !item.descartado && item.modificable);
    if (itemsNoDescartados.length === 0) {
      message.warning('No hay fardos seleccionados para resumir');
      return;
    }

    // Validar que tenemos el id_vendedor del contrato
    if (!contratoData?.vendedor_id) {
      message.error('No se encontró el ID del vendedor en el contrato');
      return;
    }

    // Obtener el cliente_id del formulario
    const formValues = form.getFieldsValue();
    const clienteId = formValues.cliente ? parseInt(formValues.cliente) : 0;

    // Ordenar los items por lote y número de fardo para mejorar el agrupamiento
    const itemsOrdenados = [...itemsNoDescartados].sort((a, b) => {
      if (a.lote !== b.lote) return a.lote - b.lote;
      return a.desde - b.desde;
    });

    // Agrupar fardos consecutivos por lote
    const grupos: Record<number, ItemDetalle[]> = {};
    
    itemsOrdenados.forEach(item => {
      const key = item.lote;
      if (!grupos[key]) {
        grupos[key] = [];
      }
      grupos[key].push(item);
    });

    // Crear resumen por grupos consecutivos
    const nuevosResumenItems: ResumenItem[] = [];
    
    Object.entries(grupos).forEach(([lote, items]) => {
      let grupoActual: ItemDetalle[] = [items[0]];
      
      for (let i = 1; i < items.length; i++) {
        const itemAnterior = items[i-1];
        const itemActual = items[i];
        
        // Si son consecutivos, agregar al grupo actual
        if (itemActual.desde === itemAnterior.desde + 1) {
          grupoActual.push(itemActual);
        } else {
          // Si no son consecutivos, finalizar el grupo actual y comenzar uno nuevo
          nuevosResumenItems.push({
            desde: grupoActual[0].desde,
            hasta: grupoActual[grupoActual.length - 1].desde,
            lote: parseInt(lote),
            cantidad: grupoActual.length
          });
          grupoActual = [itemActual];
        }
      }
      
      // Agregar el último grupo
      nuevosResumenItems.push({
        desde: grupoActual[0].desde,
        hasta: grupoActual[grupoActual.length - 1].desde,
        lote: parseInt(lote),
        cantidad: grupoActual.length
      });
    });

    // Agregar los nuevos items resumidos a los existentes
    setResumenItems(prevItems => [...prevItems, ...nuevosResumenItems]);

    // Agregar los fardos seleccionados a la lista para grabar
    // Usar el origen que estaba seleccionado en el momento de cada ciclo
    const nuevosFardosParaGrabar = itemsNoDescartados.map(item => ({
      nro_fardo: item.desde,
      nro_lote: item.lote,
      id_fardo: item.id_fardo || 0,  // Usar el id_fardo del backend si existe, sino 0
      id_orden: item.id_orden || 0,  // Usar el id_orden del backend si existe, sino 0
      origen_id: item.id_origen,  // Usando el origen almacenado en cada item
      cliente_id: clienteId  // Usando el cliente del formulario
    }));

    // Console.log para mostrar el detalle de fardos a grabar
    console.log('=== DETALLE DE FARDOS A GRABAR ===');
    console.log('Items no descartados:', itemsNoDescartados);
    console.log('Nuevos fardos para grabar:', nuevosFardosParaGrabar);
    console.log('Origen por fardo:');
    nuevosFardosParaGrabar.forEach(fardo => {
      const origenOptionResumen = origenesOptions.find(option => option.value === fardo.origen_id.toString());
      const clienteOptionResumen = clientesOptions.find(option => option.value === fardo.cliente_id.toString());
      console.log(`Fardo ${fardo.nro_fardo}: Origen ID ${fardo.origen_id} - ${origenOptionResumen?.label || 'No encontrado'}, Cliente ID ${fardo.cliente_id} - ${clienteOptionResumen?.label || 'No encontrado'}`);
    });
    console.log('================================');

    setFardosParaGrabar(prevFardos => [...prevFardos, ...nuevosFardosParaGrabar]);

    // Limpiar los datos del Detalle después de resumir
    setItemsDetalle([]);
    
    message.success(`Se resumieron ${itemsNoDescartados.length} fardos en ${nuevosResumenItems.length} grupos`);
  };

  const handleGrabarTodo = async () => {
    try {
      if (fardosParaGrabar.length === 0) {
        message.warning('No hay fardos para grabar');
        return;
      }

      if (!contratoData?.id) {
        message.error('No se encontró el ID del contrato');
        return;
      }

      // Validar que tenemos el origen seleccionado
      const formValues = form.getFieldsValue();
      const origenSeleccionado = formValues.origen;
      
      if (!origenSeleccionado) {
        message.error('Debe seleccionar un origen antes de grabar');
        return;
      }

      setGrabandoDatos(true);

      // Console.log para mostrar todos los fardos que se van a grabar
      console.log('=== TODOS LOS FARDOS A GRABAR AL BACKEND ===');
      console.log('Total de fardos:', fardosParaGrabar.length);
      console.log('Fardos completos:', fardosParaGrabar);
      console.log('Detalle por fardo:');
      fardosParaGrabar.forEach((fardo, index) => {
        const origenOptionGrabar = origenesOptions.find(option => option.value === fardo.origen_id.toString());
        const clienteOptionGrabar = clientesOptions.find(option => option.value === fardo.cliente_id.toString());
        console.log(`${index + 1}. Fardo ${fardo.nro_fardo}, Lote ${fardo.nro_lote}, Origen: ${origenOptionGrabar?.label || 'No encontrado'} (ID: ${fardo.origen_id}), Cliente: ${clienteOptionGrabar?.label || 'No encontrado'} (ID: ${fardo.cliente_id})`);
      });
      console.log('============================================');

      const token = await getAccessTokenSilently();
      const id_empresa = user?.id_empresa;

      const response = await fetch(
        `${process.env.REACT_APP_ORDENES_URL}/crear_aplicacion/${id_empresa}/${contratoData.id}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: fardosParaGrabar
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al grabar la aplicación');
      }

      const data = await response.json();
      message.success('Aplicación grabada correctamente');
      
      // Limpiar formulario y datos
      form.resetFields();
      setItemsDetalle([]);
      setResumenItems([]);
      setFardosParaGrabar([]);
      
      // Navegar a la lista de contratos después de grabar exitosamente
      navigate('/listadocontratos');
      
    } catch (error) {
      console.error('Error al grabar aplicación:', error);
      message.error(error instanceof Error ? error.message : 'Error al grabar la aplicación');
    } finally {
      setGrabandoDatos(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Spin size="large" />
        <p>Cargando datos del contrato...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (!contratoData) {
    return (
      <Alert
        message="Información"
        description="No se encontraron datos del contrato"
        type="info"
        showIcon
      />
    );
  }

  return (
    <div>
      <Card
        style={{
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%',
          marginBottom: '16px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Información del Contrato"
      >
        <Form layout="vertical" className="aplicacion-form">
          <style>{`
            .aplicacion-form .ant-form-item-label > label {
              color: #374151 !important;
              font-weight: 500 !important;
              font-size: 13px !important;
            }
            .aplicacion-form .ant-form-item {
              margin-bottom: 12px !important;
            }
            .aplicacion-form .ant-input,
            .aplicacion-form .ant-select-selector {
              border-color: #d1d5db !important;
              border-radius: 6px !important;
            }
          `}</style>
          <Row gutter={[16, 12]}>
            <Col span={6}>
              <Form.Item label="Número de Contrato">
                <Input value={contratoData?.ncontrato || ''} disabled />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Tipo de Contrato">
                <Input value={contratoData?.tipo === 'C' ? 'Compra' : 'Venta'} disabled />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Producto">
                <Input value={contratoData?.producto_nombre || ''} disabled />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Fecha">
                <Input value={contratoData?.fecha || ''} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item label="Comprador">
                <Input value={contratoData?.comprador_nombre || ''} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Vendedor">
                <Input value={contratoData?.vendedor_nombre || ''} disabled />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        style={{
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Form
          form={form}
          layout="vertical"
          className="aplicacion-form"
        >
          <style>{`
            .aplicacion-form .ant-form-item-label > label {
              color: #374151 !important;
              font-weight: 500 !important;
              font-size: 13px !important;
            }
            .aplicacion-form .ant-form-item {
              margin-bottom: 12px !important;
            }
            .aplicacion-form .ant-input,
            .aplicacion-form .ant-select-selector,
            .aplicacion-form .ant-input-number {
              border-color: #d1d5db !important;
              border-radius: 6px !important;
            }
            .aplicacion-form .ant-input:focus,
            .aplicacion-form .ant-select-selector:focus,
            .aplicacion-form .ant-input-number:focus {
              border-color: ${theme.colors.primary} !important;
              box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
            }
            .section-subtitle {
              color: white;
              text-align: center;
              padding: 8px 12px;
              margin-bottom: 12px;
              border-radius: 6px;
              font-size: 14px;
              fontWeight: 600;
            }
            .section-card {
              background-color: #f5f5f5;
              padding: 12px;
              border-radius: 6px;
              margin-bottom: 12px;
              flex: 1;
              display: flex;
              flex-direction: column;
              height: 100%;
              border: 2px solid ${theme.colors.primary};
              max-height: calc(100vh - 400px);
              overflow-y: auto;
            }
          `}</style>
          <Row gutter={16}>
            <Col span={8}>
              <div className="section-subtitle" style={{ backgroundColor: theme.colors.primary }}>
                Asignación
              </div>
              <div className="section-card">
                <Form
                  form={form}
                  layout="vertical"
                  style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                  onFinish={(e) => {
                    e.preventDefault();
                    const values = form.getFieldsValue();
                    handleSubmit(values);
                  }}
                >
                  <Form.Item 
                    label="Origen"
                    name="origen"
                    rules={[{ required: true, message: 'Por favor seleccione el origen' }]}
                  >
                    <Select 
                      placeholder="Seleccione el origen" 
                      options={origenesOptions}
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={contratoData?.tipo === 'C'}
                    />
                  </Form.Item>

                  <Form.Item 
                    label="Cliente"
                    name="cliente"
                    rules={[
                      { 
                        required: contratoData?.tipo !== 'C', 
                        message: 'Por favor seleccione el cliente' 
                      }
                    ]}
                  >
                    <Select 
                      placeholder={contratoData?.tipo === 'C' ? 'No disponible para contratos de compra' : 'Seleccione el cliente'} 
                      options={clientesOptions}
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      disabled={contratoData?.tipo === 'C'}
                    />
                  </Form.Item>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item 
                        label="Desde"
                        name="desde"
                        rules={[{ required: true, message: 'Por favor ingrese el valor' }]}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          placeholder="Desde"
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        label="Hasta"
                        name="hasta"
                        rules={[{ required: true, message: 'Por favor ingrese el valor' }]}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          placeholder="Hasta"
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item 
                        label="Lote"
                        name="lote"
                        rules={[{ required: true, message: 'Por favor ingrese el lote' }]}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          placeholder="Lote"
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    gap: '8px',
                    padding: '8px 0'
                  }}>
                    <Button 
                      type="primary" 
                      size="middle"
                      style={{ 
                        flex: 1
                      }}
                      onClick={() => {
                        const fieldsToValidate = ['origen', 'desde', 'hasta', 'lote'];
                        // Solo validar cliente si no es un contrato de compra
                        if (contratoData?.tipo !== 'C') {
                          fieldsToValidate.push('cliente');
                        }
                        
                        form.validateFields(fieldsToValidate)
                          .then(values => {
                            handleSubmit(values);
                          })
                          .catch(error => {
                            console.error('Error de validación:', error);
                          });
                      }}
                    >
                      Enviar
                    </Button>
                    <Button 
                      type="default" 
                      size="middle"
                      style={{ 
                        backgroundColor: theme.colors.success,
                        borderColor: theme.colors.success,
                        color: 'white'
                      }}
                      onClick={handleTildarTodos}
                      disabled={itemsDetalle.length === 0}
                    >
                      Tildar
                    </Button>
                    <Button 
                      type="default" 
                      size="middle"
                      style={{ 
                        backgroundColor: theme.colors.error,
                        borderColor: theme.colors.error,
                        color: 'white'
                      }}
                      onClick={handleDestildarTodos}
                      disabled={itemsDetalle.length === 0}
                    >
                      Destildar
                    </Button>
                  </div>
                </Form>
              </div>
            </Col>
            <Col span={8}>
              <div className="section-subtitle" style={{ backgroundColor: theme.colors.success }}>
                Detalle
              </div>
              <div className="section-card">
                {itemsDetalle.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                    No hay fardos en el detalle
                  </div>
                ) : (
                  itemsDetalle.map((item) => (
                    <div key={item.id} style={{ 
                      padding: '6px 8px', 
                      borderBottom: '1px solid #e8e8e8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: item.descartado ? '#fff1f0' : 'transparent',
                      opacity: item.modificable ? 1 : 0.6,
                      fontSize: '12px'
                    }}>
                      <Checkbox 
                        checked={!item.descartado}
                        onChange={() => handleDescartarItem(item.id)}
                        disabled={!item.modificable}
                        style={{ 
                          cursor: item.modificable ? 'pointer' : 'not-allowed'
                        }}
                      />
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '10px',
                        flex: 1,
                        flexWrap: 'wrap' as const
                      }}>
                        <span style={{ fontWeight: 'bold', minWidth: '40px', fontSize: '12px' }}>
                          {item.desde}
                        </span>
                        <span style={{ 
                          color: item.aplicacion === 'SI' ? theme.colors.error : theme.colors.success,
                          fontWeight: 'bold',
                          minWidth: '70px',
                          fontSize: '11px'
                        }}>
                          Apl:{item.aplicacion}
                        </span>
                        <span style={{ 
                          color: item.stock === 'SI' ? theme.colors.success : item.stock === 'NO' ? theme.colors.error : theme.colors.textSecondary,
                          fontWeight: 'bold',
                          minWidth: '60px',
                          fontSize: '11px'
                        }}>
                          Stock:{item.stock || 'N/A'}
                        </span>
                        {!item.modificable && (
                          <span style={{ 
                            fontSize: '10px',
                            color: '#999',
                            fontStyle: 'italic'
                          }}>
                            No modif.
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'center',
                padding: '8px 0',
                gap: '8px'
              }}>
                <Button 
                  type="primary" 
                  size="middle"
                  style={{ 
                    flex: 1
                  }}
                  onClick={handleResumir}
                  disabled={itemsDetalle.length === 0 || contarItemsNoDescartados() === 0}
                >
                  Resumir
                </Button>
                <div style={{ 
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#000000',
                  backgroundColor: '#ffffff',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  minWidth: '80px',
                  textAlign: 'center' as const
                }}>
                  Total: {contarItemsNoDescartados()}
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div className="section-subtitle" style={{ backgroundColor: '#722ed1' }}>
                Resumen
              </div>
              <div className="section-card">
                {resumenItems.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                    No hay items en el resumen
                  </div>
                ) : (
                  resumenItems.map((item, index) => (
                    <div key={index} style={{ 
                      padding: '6px 8px', 
                      borderBottom: '1px solid #e8e8e8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '12px',
                        flex: 1,
                        flexWrap: 'wrap' as const
                      }}>
                        <span style={{ minWidth: '60px' }}>D:{item.desde}</span>
                        <span style={{ minWidth: '60px' }}>H:{item.hasta}</span>
                        <span style={{ minWidth: '60px' }}>L:{item.lote}</span>
                        <span style={{ minWidth: '50px', fontWeight: 'bold' }}>C:{item.cantidad}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 0',
                gap: '8px'
              }}>
                <Button 
                  type="primary" 
                  size="middle"
                  style={{ 
                    backgroundColor: theme.colors.success,
                    borderColor: theme.colors.success,
                    flex: 1
                  }}
                  onClick={handleGrabarTodo}
                  disabled={resumenItems.length === 0 || grabandoDatos}
                  loading={grabandoDatos}
                >
                  Grabar Todo
                </Button>
                <div style={{ 
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#000000',
                  backgroundColor: '#ffffff',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  minWidth: '80px',
                  textAlign: 'center' as const
                }}>
                  Total: {resumenItems.reduce((acc, item) => acc + item.cantidad, 0)}
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default AltaAplicacion;
